// Phase H — BI / Reporting Dashboard (forwarder-only).
//
// Single page with a sticky filter bar driving four data hooks in
// parallel (kpis / top / trend / operational). Each card and chart
// respects the same filter snapshot — debounced by react-query staleTime.
//
// Filters
// -------
// Primary (always visible):
//   - customer_tenant_id   (typed in; we keep the picker minimal —
//                            forwarders typically type a customer name)
//   - customer_category_id (single id; multi-select packaged as csv)
//   - request_type         (tender / spot / sonderfahrt)
//   - period               (MTD / YTD / Custom)
// Secondary (expandable):
//   - postal_prefix, vehicle_type, incoterm, direction
//
// Layout: filter bar → 6 KPI cards → trend chart → operational strip
// (4 cards + claim donut) → top-lists grid (lanes / directions /
// vehicles / customers by revenue / customers by margin).
import React, { useMemo, useState } from "react";
import {
  BarChart3, Filter, ChevronDown, ChevronUp, AlertCircle, Loader2,
  TrendingUp, TrendingDown, Activity, Anchor, Truck, Users, Crown, Clock,
  Hourglass, ShieldAlert, Target, Percent, Bell, BellOff, X, ExternalLink,
  AlertTriangle, Coins, ZapOff, FileCheck2, AlertOctagon,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from "recharts";

import { useBIKpis, useBITop, useBITrend, useBIOperational } from "../api/bi";
import { useBICopq, useBIContractVsSpot, useAlertSettings, useAlertSubs, useCreateAlertSub, usePatchAlertSub, useDeleteAlertSub, useScanAlertsNow } from "../api/biExtras";
import { PerfBody, useRecurringPerfSummary } from "../components/RecurringSeriesPerfWidget";
import { Link } from "react-router-dom";
import { toast } from "sonner";

// ─── Style helpers ─────────────────────────────────────────────────────────

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const syne = (size, weight, ls) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: size,
  fontWeight: weight || 700,
  letterSpacing: ls,
});

const fmtNum = (v, d = 0) => {
  if (v == null) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
};
const CURRENCY_GLYPH = { EUR: "€", HUF: "Ft" };
const fmtMoney = (v, ccy = "EUR") => {
  if (v == null) return "—";
  const g = CURRENCY_GLYPH[ccy] || ccy;
  // HUF rarely uses decimals in business reporting; keep EUR @ 2dp.
  const d = ccy === "HUF" ? 0 : 2;
  return ccy === "HUF" ? `${fmtNum(v, d)} ${g}` : `${g} ${fmtNum(v, d)}`;
};
const fmtPct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)} %`);
const fmtMin = (v) => (v == null ? "—" : `${fmtNum(v, 0)} min`);

// ─── Filter bar ─────────────────────────────────────────────────────────────

const PERIOD_OPTIONS = [
  { value: "mtd", label: "MTD" },
  { value: "ytd", label: "YTD" },
  { value: "custom", label: "Custom" },
  { value: "all", label: "All" },
];

const REQUEST_TYPES = ["tender", "spot", "sonderfahrt"];
const DIRECTIONS = ["export", "import", "domestic", "cross_trade"];
const DISPLAY_CCYS = ["EUR", "HUF"];

const _inputStyle = {
  background: "var(--bg, #0a0e1c)",
  border: "1px solid rgba(255,255,255,0.18)",
  color: "var(--text, #f0f2f8)",
  padding: "6px 9px",
  borderRadius: 4,
  ...mono(11),
};

const FilterBar = ({ filters, setFilters }) => {
  const [showSecondary, setShowSecondary] = useState(false);
  const upd = (k, v) => setFilters((prev) => ({ ...prev, [k]: v || null }));

  return (
    <div
      className="rounded-lg p-3 mb-4 sticky top-2 z-30"
      style={{
        background: "rgba(13,15,24,0.92)",
        border: "1px solid rgba(255,255,255,0.10)",
        backdropFilter: "blur(8px)",
      }}
      data-testid="bi-filter-bar"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Filter size={14} color="#9ec3ec" />
        <span style={{ ...mono(9, "#9ec3ec", 700, "0.6px"), textTransform: "uppercase" }}>Filters</span>

        {/* Period chips */}
        <div className="flex items-center gap-0.5 ml-2" data-testid="bi-filter-period-chips">
          {PERIOD_OPTIONS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => upd("period", p.value)}
              className="px-2.5 py-1 rounded"
              style={{
                background: filters.period === p.value ? "rgba(24,95,165,0.30)" : "transparent",
                border: `1px solid ${filters.period === p.value ? "rgba(24,95,165,0.65)" : "rgba(255,255,255,0.15)"}`,
                color: filters.period === p.value ? "#9ec3ec" : "#94a3b8",
                ...mono(10, undefined, 700, "0.4px"),
                cursor: "pointer",
                textTransform: "uppercase",
              }}
              data-testid={`bi-filter-period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {filters.period === "custom" && (
          <>
            <input
              type="date"
              value={filters.date_from || ""}
              onChange={(e) => upd("date_from", e.target.value)}
              style={_inputStyle}
              data-testid="bi-filter-date-from"
            />
            <span style={mono(11, "#94a3b8")}>→</span>
            <input
              type="date"
              value={filters.date_to || ""}
              onChange={(e) => upd("date_to", e.target.value)}
              style={_inputStyle}
              data-testid="bi-filter-date-to"
            />
          </>
        )}

        {/* Request type */}
        <div className="flex items-center gap-0.5 ml-1">
          {REQUEST_TYPES.map((rt) => (
            <button
              key={rt}
              type="button"
              onClick={() => upd("request_type", filters.request_type === rt ? null : rt)}
              className="px-2 py-1 rounded"
              style={{
                background: filters.request_type === rt ? "rgba(180,83,9,0.22)" : "transparent",
                border: `1px solid ${filters.request_type === rt ? "rgba(180,83,9,0.55)" : "rgba(255,255,255,0.15)"}`,
                color: filters.request_type === rt ? "#f3b14a" : "#94a3b8",
                ...mono(9, undefined, 700, "0.5px"),
                cursor: "pointer",
                textTransform: "uppercase",
              }}
              data-testid={`bi-filter-request-type-${rt}`}
            >
              {rt}
            </button>
          ))}
        </div>

        {/* Customer typeahead (simple input — typed tenant id, optional) */}
        <input
          type="text"
          placeholder="Customer tenant id"
          value={filters.customer_tenant_id || ""}
          onChange={(e) => upd("customer_tenant_id", e.target.value)}
          style={{ ..._inputStyle, width: 180 }}
          data-testid="bi-filter-customer-tenant-id"
        />

        {/* Display currency toggle — H additions */}
        <div className="flex items-center gap-0.5 ml-1" data-testid="bi-filter-currency-chips">
          <Coins size={12} color="#9ec3ec" />
          {DISPLAY_CCYS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => upd("display_currency", c)}
              className="px-2 py-1 rounded"
              style={{
                background: filters.display_currency === c ? "rgba(10,135,84,0.22)" : "transparent",
                border: `1px solid ${filters.display_currency === c ? "rgba(10,135,84,0.55)" : "rgba(255,255,255,0.15)"}`,
                color: filters.display_currency === c ? "#0a8754" : "#94a3b8",
                ...mono(9, undefined, 700, "0.5px"),
                cursor: "pointer",
                textTransform: "uppercase",
              }}
              data-testid={`bi-filter-currency-${c}`}
            >
              {c}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setShowSecondary((s) => !s)}
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded"
          style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#c5cad8", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }}
          data-testid="bi-filter-toggle-secondary"
        >
          {showSecondary ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Secondary
        </button>
      </div>

      {showSecondary && (
        <div className="flex flex-wrap items-center gap-2 mt-2 pt-2" style={{ borderTop: "1px solid rgba(255,255,255,0.10)" }} data-testid="bi-filter-secondary">
          <input
            type="text"
            placeholder="Postal prefix (e.g. 80)"
            value={filters.postal_prefix || ""}
            onChange={(e) => upd("postal_prefix", e.target.value)}
            style={{ ..._inputStyle, width: 150 }}
            data-testid="bi-filter-postal-prefix"
          />
          <input
            type="text"
            placeholder="Vehicle type"
            value={filters.vehicle_type || ""}
            onChange={(e) => upd("vehicle_type", e.target.value)}
            style={{ ..._inputStyle, width: 130 }}
            data-testid="bi-filter-vehicle-type"
          />
          <input
            type="text"
            placeholder="Incoterm (e.g. EXW)"
            value={filters.incoterm || ""}
            onChange={(e) => upd("incoterm", e.target.value)}
            style={{ ..._inputStyle, width: 130 }}
            data-testid="bi-filter-incoterm"
          />
          <select
            value={filters.direction || ""}
            onChange={(e) => upd("direction", e.target.value)}
            style={{ ..._inputStyle, width: 140 }}
            data-testid="bi-filter-direction"
          >
            <option value="">Direction (any)</option>
            {DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      )}
    </div>
  );
};

// ─── KPI card ──────────────────────────────────────────────────────────────

const VariancePill = ({ variance, testid }) => {
  if (!variance || !variance.available) {
    return (
      <span style={{ ...mono(8, "#94a3b8", 700, "0.4px"), textTransform: "uppercase" }} data-testid={testid}>
        no plan
      </span>
    );
  }
  const positive = variance.percent >= 0;
  const tone = positive ? "#0a8754" : "#ef4444";
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded"
      style={{
        background: `${tone}15`,
        border: `1px solid ${tone}55`,
        ...mono(8, tone, 700, "0.5px"),
        textTransform: "uppercase",
      }}
      data-testid={testid}
    >
      {positive ? "▲" : "▼"} {Math.abs(variance.percent).toFixed(1)}%
    </span>
  );
};

// J.5 — Freight-count breakdown tile (executed / won / rejected).
const FreightBreakdownTile = ({ label, value, tone, pct, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <div
      className="rounded-md flex items-center justify-center"
      style={{ width: 36, height: 36, background: `${tone}22`, border: `1px solid ${tone}55`, color: tone, ...syne(15, 800) }}
    >
      ●
    </div>
    <div>
      <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>{label}</div>
      <div className="flex items-baseline gap-2">
        <span style={{ ...syne(20, 800, "-0.4px"), color: tone }}>{value}</span>
        {pct != null && (
          <span style={mono(10, "#94a3b8")}>{pct.toFixed(1)} %</span>
        )}
      </div>
    </div>
  </div>
);

const KpiCard = ({ label, value, sub, icon: Icon, tone = "#9ec3ec", testid, variance, planned }) => (
  <div
    className="rounded-lg p-3"
    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}
    data-testid={testid}
  >
    <div className="flex items-center justify-between gap-1.5 mb-1.5">
      <div className="flex items-center gap-1.5">
        <Icon size={12} color={tone} />
        <span style={{ ...mono(8, "#94a3b8", 700, "0.6px"), textTransform: "uppercase" }}>{label}</span>
      </div>
      {variance !== undefined && <VariancePill variance={variance} testid={`${testid}-variance`} />}
    </div>
    <div style={{ ...syne(20, 800, "-0.4px"), color: tone }} data-testid={`${testid}-value`}>
      {value}
    </div>
    {planned != null && (
      <div className="mt-1" style={mono(9, "#94a3b8", 700, "0.3px")} data-testid={`${testid}-planned`}>
        plan: {planned}
      </div>
    )}
    {sub && <div className="mt-1" style={mono(9, "#94a3b8", 600, "0.3px")}>{sub}</div>}
  </div>
);

// ─── Top-list block ────────────────────────────────────────────────────────

const TopList = ({ title, icon: Icon, rows, formatLabel, testid, valueLabel = "Revenue", ccy = "EUR" }) => (
  <div
    className="rounded-lg p-3"
    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 mb-2">
      <Icon size={13} color="#9ec3ec" />
      <span style={{ ...mono(9, "#9ec3ec", 700, "0.6px"), textTransform: "uppercase" }}>{title}</span>
    </div>
    {(!rows || rows.length === 0) ? (
      <div style={{ ...mono(11, "#94a3b8"), padding: 8, textAlign: "center" }}>No data in slice.</div>
    ) : (
      <div className="flex flex-col gap-1.5">
        {rows.map((r, i) => (
          <div
            key={i}
            className="flex items-center justify-between gap-2 p-1.5 rounded"
            style={{ background: "rgba(0,0,0,0.18)" }}
            data-testid={`${testid}-row-${i}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span style={{ ...mono(9, "#94a3b8", 700, "0.4px"), minWidth: 16 }}>#{i + 1}</span>
              <span style={mono(11, "#f0f2f8", 700)} className="truncate">{formatLabel ? formatLabel(r) : r.label}</span>
              <span style={mono(9, "#94a3b8")}>· {r.count}</span>
            </div>
            <span style={mono(11, "#0a8754", 700)} title={`${valueLabel}: ${fmtMoney(r.revenue, ccy)}`}>{fmtMoney(r.revenue, ccy)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ─── COPQ — Cost of Poor Quality (headline block + drill-down modal) ──────

const _copqTile = (label, value, tone, testid, sub) => (
  <div className="flex-1 min-w-[140px] rounded-md p-2.5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid={testid}>
    <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>{label}</div>
    <div style={{ ...syne(18, 800, "-0.3px"), color: tone }} data-testid={`${testid}-value`}>{value}</div>
    {sub && <div className="mt-0.5" style={mono(9, "#94a3b8", 600, "0.3px")}>{sub}</div>}
  </div>
);

const CopqBlock = ({ copqQ, ccy, onDrill }) => {
  const c = copqQ.data;
  const isLoading = copqQ.isLoading;
  return (
    <div
      className="rounded-lg p-3 mb-4"
      style={{ background: "linear-gradient(145deg, rgba(239,68,68,0.06), rgba(180,83,9,0.04))", border: "1px solid rgba(239,68,68,0.32)" }}
      data-testid="bi-copq-block"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ZapOff size={15} color="#ef4444" />
          <span style={{ ...mono(11, "#ef4444", 700, "0.6px"), textTransform: "uppercase" }}>COPQ · Cost of Poor Quality</span>
          {isLoading && <Loader2 size={11} className="animate-spin" color="#94a3b8" />}
        </div>
        <button
          type="button"
          onClick={onDrill}
          disabled={!c || (c.claim_count === 0)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded"
          style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.20)", color: "#9ec3ec", ...mono(9, undefined, 700, "0.4px"), cursor: c && c.claim_count > 0 ? "pointer" : "not-allowed", textTransform: "uppercase", opacity: c && c.claim_count > 0 ? 1 : 0.5 }}
          data-testid="bi-copq-drill-button"
        >
          <ExternalLink size={10} /> Drill-down ({c?.claim_count ?? 0})
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {_copqTile("COPQ TOTAL", fmtMoney(c?.copq_total, ccy), "#ef4444", "bi-copq-total")}
        {_copqTile("Claim cost", fmtMoney(c?.claim_total_cost, ccy), "#ef4444", "bi-copq-claim-cost", `${fmtNum(c?.claim_count)} claims`)}
        {_copqTile("Waiting", fmtMoney(c?.waiting_amount_total, ccy), "#f3b14a", "bi-copq-waiting", "surcharge cost")}
        {_copqTile("Damage", fmtMoney(c?.damage_amount_total, ccy), "#b45309", "bi-copq-damage", "surcharge cost")}
        {_copqTile("Shortage", fmtMoney(c?.shortage_amount_total, ccy), "#b45309", "bi-copq-shortage", "surcharge cost")}
        {_copqTile("Delay (info)", fmtMin(c?.delay_total_minutes), "#94a3b8", "bi-copq-delay", "carrier minutes · not in total")}
      </div>
    </div>
  );
};

const CopqDrillModal = ({ rows, truncated, ccy, onClose }) => (
  <div
    className="fixed inset-0 flex items-center justify-center z-50 px-3"
    style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }}
    data-testid="bi-copq-drill-modal"
    onClick={onClose}
  >
    <div
      className="rounded-lg w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col"
      style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.18)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between p-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
        <div className="flex items-center gap-2">
          <ZapOff size={14} color="#ef4444" />
          <span style={{ ...mono(11, "#ef4444", 700, "0.5px"), textTransform: "uppercase" }}>COPQ Drill-down · {rows.length} claim{rows.length === 1 ? "" : "s"}{truncated ? " (truncated)" : ""}</span>
        </div>
        <button onClick={onClose} className="p-1 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", cursor: "pointer" }} data-testid="bi-copq-drill-close">
          <X size={13} color="#c5cad8" />
        </button>
      </div>
      <div className="overflow-y-auto p-2">
        {rows.length === 0 ? (
          <div style={{ ...mono(11, "#94a3b8"), padding: 16, textAlign: "center" }}>No claims in slice.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", ...mono(10) }}>
            <thead>
              <tr style={{ background: "rgba(0,0,0,0.30)" }}>
                <th style={{ ...mono(9, "#94a3b8", 700, "0.5px"), padding: "8px 8px", textAlign: "left", textTransform: "uppercase" }}>Deal</th>
                <th style={{ ...mono(9, "#94a3b8", 700, "0.5px"), padding: "8px 8px", textAlign: "left", textTransform: "uppercase" }}>Type</th>
                <th style={{ ...mono(9, "#94a3b8", 700, "0.5px"), padding: "8px 8px", textAlign: "right", textTransform: "uppercase" }}>Amount</th>
                <th style={{ ...mono(9, "#94a3b8", 700, "0.5px"), padding: "8px 8px", textAlign: "left", textTransform: "uppercase" }}>Status</th>
                <th style={{ ...mono(9, "#94a3b8", 700, "0.5px"), padding: "8px 8px", textAlign: "left", textTransform: "uppercase" }}>Filed</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} data-testid={`bi-copq-drill-row-${i}`}>
                  <td style={{ padding: "6px 8px", ...mono(11, "#f0f2f8", 700) }}>{r.deal_name || r.deal_id}</td>
                  <td style={{ padding: "6px 8px", ...mono(10, "#c5cad8") }}>{r.claim_type}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", ...mono(11, "#ef4444", 700) }}>{fmtMoney(r.claim_amount, ccy)}</td>
                  <td style={{ padding: "6px 8px", ...mono(10, "#94a3b8") }}>{r.claim_status || "—"}</td>
                  <td style={{ padding: "6px 8px", ...mono(10, "#94a3b8") }}>{r.created_at ? r.created_at.substring(0, 10) : "—"}</td>
                  <td style={{ padding: "6px 8px" }}>
                    <Link to={`/logistics-requests/${r.deal_id}`} className="inline-flex items-center gap-1" style={mono(9, "#9ec3ec", 700)} data-testid={`bi-copq-drill-open-${i}`}>
                      <ExternalLink size={10} /> Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </div>
);

// ─── Phase J.3 · Contract-vs-Spot block + per-customer table ───────────────

const ContractVsSpotBlock = ({ q, ccy }) => {
  const d = q.data;
  const isLoading = q.isLoading;
  const totals = d?.totals || {
    contract: { count: 0, revenue: 0, margin: 0 },
    deviation_pending: { count: 0, revenue: 0, margin: 0 },
    spot: { count: 0, revenue: 0, margin: 0 },
  };
  const grandRev = (totals.contract?.revenue || 0) + (totals.deviation_pending?.revenue || 0) + (totals.spot?.revenue || 0);
  const grandCount = (totals.contract?.count || 0) + (totals.deviation_pending?.count || 0) + (totals.spot?.count || 0);
  const share = (v) => (grandRev > 0 ? ((v || 0) / grandRev) * 100 : 0);

  const tile = (label, value, tone, testid, sub) => (
    <div
      className="flex-1 min-w-[140px] rounded-md p-2"
      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.10)" }}
      data-testid={testid}
    >
      <div style={{ ...mono(9, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>{label}</div>
      <div style={{ ...syne(18, 800, "-0.4px"), color: tone, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ ...mono(9, "#94a3b8"), marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const rows = d?.per_customer || [];

  return (
    <div
      className="rounded-lg p-3 mb-4"
      style={{ background: "linear-gradient(145deg, rgba(13,107,57,0.06), rgba(24,95,165,0.04))", border: "1px solid rgba(13,107,57,0.32)" }}
      data-testid="bi-contract-vs-spot-block"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileCheck2 size={15} color="#0a8754" />
          <span style={{ ...mono(11, "#0a8754", 700, "0.6px"), textTransform: "uppercase" }}>Contract vs Spot · Per customer ({ccy})</span>
          {isLoading && <Loader2 size={11} className="animate-spin" color="#94a3b8" />}
        </div>
        <span style={{ ...mono(9, "#94a3b8", 700, "0.4px"), textTransform: "uppercase" }} data-testid="bi-contract-vs-spot-grand-count">
          {grandCount} freight{grandCount === 1 ? "" : "s"} in slice
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {tile(
          "Contract",
          fmtMoney(totals.contract?.revenue, ccy),
          "#0a8754",
          "bi-contract-vs-spot-contract-tile",
          `${fmtNum(totals.contract?.count)} freights · ${share(totals.contract?.revenue).toFixed(1)}%`
        )}
        {tile(
          "Deviation pending",
          fmtMoney(totals.deviation_pending?.revenue, ccy),
          "#e6a700",
          "bi-contract-vs-spot-deviation-tile",
          `${fmtNum(totals.deviation_pending?.count)} freights · ${share(totals.deviation_pending?.revenue).toFixed(1)}%`
        )}
        {tile(
          "Spot",
          fmtMoney(totals.spot?.revenue, ccy),
          "#9ec3ec",
          "bi-contract-vs-spot-spot-tile",
          `${fmtNum(totals.spot?.count)} freights · ${share(totals.spot?.revenue).toFixed(1)}%`
        )}
        {tile(
          "Contract share",
          grandRev > 0 ? `${share(totals.contract?.revenue).toFixed(1)}%` : "—",
          "#0a8754",
          "bi-contract-vs-spot-share-tile",
          "of revenue"
        )}
      </div>

      <div className="rounded" style={{ background: "rgba(0,0,0,0.20)", border: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="grid items-center px-2 py-1.5" style={{ gridTemplateColumns: "1.4fr 0.9fr 0.9fr 0.9fr 0.7fr 0.7fr", borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
          {["Customer", `Contract (${ccy})`, `Dev. pending (${ccy})`, `Spot (${ccy})`, "Contract %", "Freights"].map((h, i) => (
            <div key={h} style={{ ...mono(9, "#94a3b8", 700, "0.5px"), textTransform: "uppercase", textAlign: i === 0 ? "left" : "right" }}>{h}</div>
          ))}
        </div>
        {rows.length === 0 && !isLoading && (
          <div className="px-3 py-4" style={{ ...mono(10, "#94a3b8"), textAlign: "center" }} data-testid="bi-contract-vs-spot-empty">No deals match the current filters.</div>
        )}
        {rows.slice(0, 12).map((row) => {
          const cnt = (row.contract?.count || 0) + (row.deviation_pending?.count || 0) + (row.spot?.count || 0);
          const cs = row.contract_share != null ? `${(row.contract_share * 100).toFixed(1)}%` : "—";
          const hasDev = (row.deviation_pending?.count || 0) > 0;
          return (
            <div
              key={row.customer_tenant_id}
              className="grid items-center px-2 py-1.5"
              style={{ gridTemplateColumns: "1.4fr 0.9fr 0.9fr 0.9fr 0.7fr 0.7fr", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
              data-testid={`bi-contract-vs-spot-row-${row.customer_tenant_id}`}
            >
              <div className="flex items-center gap-1.5" style={{ ...mono(11, "#f0f2f8", 700), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {hasDev && <AlertOctagon size={11} color="#e6a700" />}
                <span title={row.customer_tenant_id}>{row.customer_tenant_id}</span>
              </div>
              <div style={{ ...mono(10, "#0a8754", 700), textAlign: "right" }}>{fmtMoney(row.contract?.revenue, ccy)}</div>
              <div style={{ ...mono(10, hasDev ? "#e6a700" : "#94a3b8", 700), textAlign: "right" }}>{fmtMoney(row.deviation_pending?.revenue, ccy)}</div>
              <div style={{ ...mono(10, "#9ec3ec", 700), textAlign: "right" }}>{fmtMoney(row.spot?.revenue, ccy)}</div>
              <div style={{ ...mono(10, "#c5cad8", 700), textAlign: "right" }} data-testid={`bi-contract-vs-spot-share-${row.customer_tenant_id}`}>{cs}</div>
              <div style={{ ...mono(10, "#94a3b8"), textAlign: "right" }}>{cnt}</div>
            </div>
          );
        })}
        {rows.length > 12 && (
          <div className="px-2 py-1" style={{ ...mono(9, "#94a3b8"), textAlign: "right" }} data-testid="bi-contract-vs-spot-truncated">
            +{rows.length - 12} more customers (refine filters to see)
          </div>
        )}
      </div>
    </div>
  );
};


// ─── Plan-alerts panel ─────────────────────────────────────────────────────

const PlanAlertsPanel = () => {
  const settings = useAlertSettings();
  const subs = useAlertSubs();
  const createSub = useCreateAlertSub();
  const patchSub = usePatchAlertSub();
  const deleteSub = useDeleteAlertSub();
  const scanNow = useScanAlertsNow();
  const [newCust, setNewCust] = useState("");

  const onAdd = async (customer_tenant_id) => {
    try {
      await createSub.mutateAsync({ customer_tenant_id: customer_tenant_id || null, enabled: true });
      toast.success("Subscribed");
      setNewCust("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to subscribe");
    }
  };

  const onToggle = async (sub) => {
    try {
      await patchSub.mutateAsync({ sid: sub.id, patch: { enabled: !sub.enabled } });
    } catch (e) {
      toast.error("Failed");
    }
  };

  const onDelete = async (sid) => {
    if (!window.confirm("Remove subscription?")) return;
    try {
      await deleteSub.mutateAsync(sid);
      toast.success("Removed");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const onScan = async () => {
    try {
      const r = await scanNow.mutateAsync();
      toast.success(`Scan complete · ${r.fired} alert${r.fired === 1 ? "" : "s"} fired`);
    } catch (e) {
      toast.error("Scan failed");
    }
  };

  const t = settings.data?.threshold_percent;
  return (
    <div data-testid="bi-alerts-tab-content">
      <div className="rounded-lg p-3 mb-3 flex items-center justify-between" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="bi-alerts-threshold-row">
        <div>
          <div style={{ ...mono(9, "#94a3b8", 700, "0.6px"), textTransform: "uppercase" }}>Active threshold (set by platform admin)</div>
          <div style={{ ...syne(22, 800, "-0.3px"), color: t != null && t < 0 ? "#ef4444" : "#9ec3ec" }} data-testid="bi-alerts-threshold-value">
            {t != null ? `${t.toFixed(1)} %` : "—"}
          </div>
          <div style={mono(10, "#94a3b8")}>Subscribers get alerted when MTD margin variance drops to or below this number.</div>
        </div>
        <button onClick={onScan} disabled={scanNow.isPending} className="inline-flex items-center gap-1 px-3 py-1.5 rounded" style={{ background: "#185FA5", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase", opacity: scanNow.isPending ? 0.7 : 1 }} data-testid="bi-alerts-scan-now">
          <Bell size={11} /> {scanNow.isPending ? "Scanning…" : "Scan now"}
        </button>
      </div>

      <div className="rounded-lg p-3 mb-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="bi-alerts-new-row">
        <div style={{ ...mono(9, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }} className="mb-1.5">Add subscription</div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="customer_tenant_id (leave blank = all-customers baseline)"
            value={newCust}
            onChange={(e) => setNewCust(e.target.value)}
            style={{ ..._inputStyle, flex: 1, minWidth: 280 }}
            data-testid="bi-alerts-new-customer"
          />
          <button onClick={() => onAdd(newCust)} disabled={createSub.isPending} className="px-3 py-1.5 rounded inline-flex items-center gap-1" style={{ background: "#0a8754", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }} data-testid="bi-alerts-new-submit">
            <Bell size={11} /> Subscribe
          </button>
        </div>
      </div>

      <div className="rounded-lg" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="bi-alerts-subs-list">
        <div className="px-3 py-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", ...mono(9, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Your subscriptions ({subs.data?.length ?? 0})</div>
        {(!subs.data || subs.data.length === 0) ? (
          <div style={{ ...mono(11, "#94a3b8"), padding: 16, textAlign: "center" }}>No subscriptions yet.</div>
        ) : (
          subs.data.map((s) => (
            <div key={s.id} className="px-3 py-2 flex items-center justify-between gap-2" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} data-testid={`bi-alerts-sub-${s.id}`}>
              <div className="flex flex-col">
                <span style={mono(11, "#f0f2f8", 700)}>{s.customer_tenant_id ? `Customer: ${s.customer_tenant_id}` : "All customers (baseline)"}</span>
                <span style={mono(9, "#94a3b8")}>
                  {s.last_triggered_at ? `Last fired ${s.last_triggered_at.substring(0, 10)} · variance ${s.last_variance_percent?.toFixed?.(1) ?? "—"}%` : "Not yet fired"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => onToggle(s)} className="inline-flex items-center gap-1 px-2 py-1 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: s.enabled ? "#0a8754" : "#94a3b8", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }} data-testid={`bi-alerts-sub-toggle-${s.id}`}>
                  {s.enabled ? <Bell size={10} /> : <BellOff size={10} />} {s.enabled ? "ON" : "OFF"}
                </button>
                <button onClick={() => onDelete(s.id)} className="p-1 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", cursor: "pointer" }} data-testid={`bi-alerts-sub-delete-${s.id}`}>
                  <X size={11} color="#ef4444" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────

const DEFAULT_FILTERS = {
  period: "ytd",
  customer_tenant_id: null,
  customer_category_id: null,
  request_type: null,
  date_from: null,
  date_to: null,
  postal_prefix: null,
  vehicle_type: null,
  incoterm: null,
  direction: null,
  display_currency: "EUR",
};

export default function BIDashboard() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const initialTab = (() => {
    if (typeof window === "undefined") return "overview";
    const p = new URLSearchParams(window.location.search).get("tab");
    return ["overview", "recurring", "alerts"].includes(p) ? p : "overview";
  })();
  const [tab, setTab] = useState(initialTab); // "overview" | "recurring" | "alerts"
  const [copqDrillOpen, setCopqDrillOpen] = useState(false);

  const kpis = useBIKpis(filters);
  const top = useBITop(filters, 10);
  const trendQ = useBITrend(filters);
  const op = useBIOperational(filters);
  const copqQ = useBICopq(filters);
  const cvsQ = useBIContractVsSpot(filters);
  const recurringSummary = useRecurringPerfSummary();

  const ccy = filters.display_currency || "EUR";

  const isFwdInternalDenied = useMemo(() => {
    const codes = [kpis.error?.response?.status, top.error?.response?.status, trendQ.error?.response?.status, op.error?.response?.status];
    return codes.some((c) => c === 403);
  }, [kpis.error, top.error, trendQ.error, op.error]);

  if (isFwdInternalDenied) {
    return (
      <div data-testid="bi-dashboard-403" className="rounded-lg p-6 mt-6" style={{ background: "rgba(220,53,69,0.08)", border: "1px solid rgba(220,53,69,0.30)" }}>
        <AlertCircle size={18} color="#ef4444" className="inline mr-2" />
        <span style={mono(12, "#ef4444", 700)}>This dashboard is forwarder-internal-only. Your account does not have access.</span>
      </div>
    );
  }

  const k = kpis.data;
  const trendBuckets = trendQ.data?.buckets || [];
  const oRow = op.data;

  return (
    <div data-testid="bi-dashboard-page">
      <header className="mb-4">
        <h1 style={syne("clamp(24px, 3vw, 30px)", 800, "-0.5px")} data-testid="view-title">
          <BarChart3 size={22} className="inline mr-2" />
          BI · Live Dashboard
        </h1>
        <div className="mt-1" style={mono(11, "#94a3b8")}>
          Revenue / volume / margin · operational · top lists · trend · planned vs actual lands in H.5
        </div>
      </header>

      <FilterBar filters={filters} setFilters={setFilters} />

      {/* H.4 — Tab switcher: Overview (the existing dashboard) | Recurring */}
      <div className="flex items-center gap-1 mb-3" data-testid="bi-dashboard-tabs">
        {[
          { value: "overview", label: "Overview" },
          { value: "recurring", label: "Recurring series" },
          { value: "alerts", label: "Plan alerts" },
        ].map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setTab(t.value)}
            className="px-3 py-1.5 rounded"
            style={{
              background: tab === t.value ? "rgba(24,95,165,0.30)" : "transparent",
              border: `1px solid ${tab === t.value ? "rgba(24,95,165,0.65)" : "rgba(255,255,255,0.15)"}`,
              color: tab === t.value ? "#9ec3ec" : "#94a3b8",
              ...mono(10, undefined, 700, "0.4px"),
              cursor: "pointer",
              textTransform: "uppercase",
            }}
            data-testid={`bi-dashboard-tab-${t.value}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "recurring" ? (
        <div data-testid="bi-recurring-tab-content">
          {recurringSummary.isLoading && (
            <div style={mono(11, "#94a3b8")}><Loader2 size={12} className="inline animate-spin mr-2" />Loading recurring perf…</div>
          )}
          {recurringSummary.data && (
            <>
              <div className="mb-3" style={mono(10, "#94a3b8", 700, "0.4px")}>OVERALL ACROSS {recurringSummary.data.per_template.length} TEMPLATES · ON-TIME FALLBACK {recurringSummary.data.fallback_days}d</div>
              <PerfBody perf={recurringSummary.data.overall} testid="bi-recurring-overall" />
              <h3 className="mt-5 mb-2" style={syne(14, 700)}>Per template</h3>
              <div className="flex flex-col gap-2" data-testid="bi-recurring-per-template-list">
                {recurringSummary.data.per_template.map((p) => (
                  <div key={p.template_id} className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid={`bi-recurring-template-${p.template_id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span style={syne(13, 700)}>{p.template_name}</span>
                      <div className="flex items-center gap-3" style={mono(10)}>
                        <span style={mono(10, "#9ec3ec", 700)}>{p.runs} runs</span>
                        <span style={mono(10, "#0a8754", 700)}>{p.on_time_rate == null ? "—" : `${(p.on_time_rate * 100).toFixed(1)}% on-time`}</span>
                        <span style={mono(10, "#0a8754", 700)}>€ {Number(p.avg_margin_per_leg).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} avg/leg</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : tab === "alerts" ? (
        <PlanAlertsPanel />
      ) : (
      <>
      {/* Missing FX rate banner — surfaces months in the slice that lack an admin rate */}
      {(() => {
        const merged = new Set();
        for (const m of (k?.missing_rate_months || [])) merged.add(`${m.year}-${String(m.month).padStart(2, "0")}`);
        for (const m of (oRow?.missing_rate_months || [])) merged.add(`${m.year}-${String(m.month).padStart(2, "0")}`);
        for (const m of (copqQ.data?.missing_rate_months || [])) merged.add(`${m.year}-${String(m.month).padStart(2, "0")}`);
        for (const m of (top.data?.missing_rate_months || [])) merged.add(`${m.year}-${String(m.month).padStart(2, "0")}`);
        for (const m of (trendQ.data?.missing_rate_months || [])) merged.add(`${m.year}-${String(m.month).padStart(2, "0")}`);
        if (merged.size === 0) return null;
        return (
          <div className="rounded-lg p-2.5 mb-3 flex items-start gap-2"
               style={{ background: "rgba(180,83,9,0.10)", border: "1px solid rgba(180,83,9,0.45)" }}
               data-testid="bi-missing-rate-banner">
            <AlertTriangle size={14} color="#f3b14a" className="mt-0.5" />
            <div className="flex-1">
              <div style={{ ...mono(10, "#f3b14a", 700, "0.4px"), textTransform: "uppercase" }}>
                Missing EUR/HUF rates · deals in these months were excluded from {ccy} totals
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {Array.from(merged).sort().map((m) => (
                  <span key={m} className="px-1.5 py-0.5 rounded" style={{ background: "rgba(180,83,9,0.18)", border: "1px solid rgba(180,83,9,0.30)", ...mono(9, "#f3b14a", 700) }} data-testid={`bi-missing-rate-${m}`}>{m}</span>
                ))}
                <Link to="/admin/bi-fx-rates" className="ml-1 inline-flex items-center gap-1" style={mono(9, "#9ec3ec", 700)} data-testid="bi-missing-rate-fix-link">
                  <ExternalLink size={10} /> Open FX admin
                </Link>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Core KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4" data-testid="bi-kpi-grid">
        <KpiCard label="Revenue" tone="#0a8754" icon={TrendingUp} value={fmtMoney(k?.actual_revenue, ccy)} sub={k?.filters_resolved?.period?.toUpperCase()} testid="bi-kpi-revenue" variance={k?.variance_revenue} planned={k?.planned_revenue != null ? fmtMoney(k.planned_revenue, ccy) : null} />
        <KpiCard label="Freight count" tone="#9ec3ec" icon={Truck} value={fmtNum(k?.actual_freight_count)} sub={`pipeline · ${k?.actual_executed_freight_count != null ? fmtNum(k.actual_executed_freight_count) + " executed" : k?.filters_resolved?.period?.toUpperCase()}`} testid="bi-kpi-freight-count" variance={k?.variance_freight_count} planned={k?.planned_freight_count != null ? fmtNum(k.planned_freight_count) : null} />
        <KpiCard label="Avg rate" tone="#9ec3ec" icon={Activity} value={fmtMoney(k?.actual_avg_rate, ccy)} sub="per freight" testid="bi-kpi-avg-rate" />
        <KpiCard label="Non-transport rev." tone="#b45309" icon={Anchor} value={fmtMoney(k?.actual_non_transport_revenue, ccy)} sub="surcharges (waiting/insurance/added)" testid="bi-kpi-non-transport" />
        <KpiCard label="Transport ratio" tone="#9ec3ec" icon={Percent} value={fmtPct(k?.actual_transport_other_ratio)} sub="transport ÷ total" testid="bi-kpi-ratio" />
        <KpiCard label="Margin" tone="#0a8754" icon={TrendingDown} value={fmtMoney(k?.actual_margin, ccy)} sub="fuvarozas + szallit. + added" testid="bi-kpi-margin" variance={k?.variance_margin} planned={k?.planned_margin != null ? fmtMoney(k.planned_margin, ccy) : null} />
      </div>

      {/* J.5 — Freight-count breakdown strip: executed / won / rejected_by_provider
          side by side, respecting the active filters. */}
      <div className="rounded-md p-3 mb-4 grid grid-cols-3 gap-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="bi-freight-breakdown">
        <FreightBreakdownTile
          label="Executed (teljesült)"
          value={fmtNum(k?.actual_executed_freight_count)}
          tone="#0a8754"
          pct={k?.actual_freight_count ? ((k?.actual_executed_freight_count || 0) / k.actual_freight_count * 100) : null}
          testid="bi-freight-breakdown-executed"
        />
        <FreightBreakdownTile
          label="Won (offer accepted)"
          value={fmtNum(k?.actual_won_freight_count)}
          tone="#9ec3ec"
          pct={k?.actual_freight_count ? ((k?.actual_won_freight_count || 0) / k.actual_freight_count * 100) : null}
          testid="bi-freight-breakdown-won"
        />
        <FreightBreakdownTile
          label="Rejected by provider"
          value={fmtNum(k?.actual_rejected_by_provider_freight_count)}
          tone="#7e3aa8"
          pct={k?.actual_freight_count ? ((k?.actual_rejected_by_provider_freight_count || 0) / k.actual_freight_count * 100) : null}
          testid="bi-freight-breakdown-rejected"
        />
      </div>

      {/* Currency mix chip */}
      {k?.currency_mix && k.currency_mix.length > 0 && (
        <div className="mb-4 flex items-center gap-2" data-testid="bi-currency-mix">
          <span style={{ ...mono(9, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Currency mix</span>
          {k.currency_mix.map((c) => (
            <span key={c.currency} className="px-2 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", ...mono(10, "#c5cad8", 700) }} data-testid={`bi-currency-${c.currency}`}>
              {c.currency}: {(c.share * 100).toFixed(1)}%
            </span>
          ))}
        </div>
      )}

      {/* Trend chart */}
      <div className="rounded-lg p-3 mb-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="bi-trend-chart">
        <div className="flex items-center gap-2 mb-2">
          <Activity size={14} color="#9ec3ec" />
          <span style={{ ...mono(10, "#9ec3ec", 700, "0.5px"), textTransform: "uppercase" }}>Monthly trend · revenue / freight count / margin ({ccy})</span>
        </div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={trendBuckets} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="bucket" tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} tickFormatter={(v) => {
                if (v >= 1_000_000) return `${(v/1_000_000).toFixed(1)}M`;
                if (v >= 1000) return `${(v/1000).toFixed(0)}k`;
                return String(v);
              }} />
              <Tooltip
                contentStyle={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 4, color: "#f0f2f8", fontFamily: "'DM Mono', monospace", fontSize: 11 }}
                formatter={(value, name) => {
                  if (name === "Freight count") return [value, name];
                  return [fmtMoney(value, ccy), name];
                }}
              />
              <Legend wrapperStyle={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8" }} />
              <Line type="monotone" dataKey="revenue" stroke="#0a8754" strokeWidth={2} dot={false} name="Revenue" />
              <Line type="monotone" dataKey="margin" stroke="#9ec3ec" strokeWidth={2} dot={false} name="Margin" />
              <Line type="monotone" dataKey="count" stroke="#f3b14a" strokeWidth={2} dot={false} name="Freight count" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Operational strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2" data-testid="bi-operational-grid">
        <KpiCard label="Delay total (carrier)" tone="#ef4444" icon={Clock} value={fmtMin(oRow?.delay_total_minutes)} sub={`avg ${fmtMin(oRow?.delay_avg_minutes)}`} testid="bi-op-delay-total" />
        <KpiCard label="Waiting total" tone="#f3b14a" icon={Hourglass} value={fmtMin(oRow?.waiting_total_minutes)} sub={`avg ${fmtMin(oRow?.waiting_avg_minutes)} · ${fmtMoney(oRow?.waiting_total_amount, ccy)}`} testid="bi-op-waiting-total" />
        <KpiCard label="Claim cost" tone="#ef4444" icon={ShieldAlert} value={fmtMoney(oRow?.claim_total_cost, ccy)} sub={`${fmtNum(oRow?.claim_count)} claims · avg ${fmtMoney(oRow?.claim_avg_cost_per_freight, ccy)}/freight`} testid="bi-op-claim-cost" />
        <KpiCard label="On-time rate" tone="#0a8754" icon={Target} value={fmtPct(oRow?.on_time_rate)} sub={`${oRow?.on_time_hit ?? 0} / ${oRow?.on_time_eligible ?? 0} executed`} testid="bi-op-on-time" />
      </div>

      {/* Claim donut */}
      {oRow?.claim_type_distribution && oRow.claim_type_distribution.length > 0 && (
        <div className="rounded-lg p-3 mb-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="bi-claim-donut">
          <div style={{ ...mono(10, "#9ec3ec", 700, "0.5px"), textTransform: "uppercase" }} className="mb-2">Claim type distribution</div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={oRow.claim_type_distribution} dataKey="count" nameKey="claim_type" innerRadius={50} outerRadius={85} paddingAngle={2}>
                  {oRow.claim_type_distribution.map((_, i) => (
                    <Cell key={i} fill={["#ef4444", "#f3b14a", "#9ec3ec", "#0a8754", "#b45309", "#94a3b8"][i % 6]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 4, color: "#f0f2f8", fontFamily: "'DM Mono', monospace", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* COPQ — Cost of Poor Quality, full-width headline block */}
      <CopqBlock copqQ={copqQ} ccy={ccy} onDrill={() => setCopqDrillOpen(true)} />

      {/* J.3 — Contract-vs-Spot per-customer breakdown */}
      <ContractVsSpotBlock q={cvsQ} ccy={ccy} />

      {/* Top lists grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bi-top-grid">
        <TopList title="Top lanes (country pair)" icon={Anchor} rows={top.data?.lanes} testid="bi-top-lanes" formatLabel={(r) => r.label} ccy={ccy} />
        <TopList title="Top directions" icon={TrendingUp} rows={top.data?.directions} testid="bi-top-directions" ccy={ccy} />
        <TopList title="Top vehicle types" icon={Truck} rows={top.data?.vehicles} testid="bi-top-vehicles" ccy={ccy} />
        <TopList title="Top customers by revenue" icon={Crown} rows={top.data?.top_customers_by_revenue} testid="bi-top-customers-revenue" ccy={ccy} />
        <TopList title="Top customers by margin" icon={Users} rows={top.data?.top_customers_by_margin} testid="bi-top-customers-margin" valueLabel="Margin" formatLabel={(r) => `${r.label} · ${fmtMoney(r.margin, ccy)}`} ccy={ccy} />
      </div>

      {/* Loading state */}
      {(kpis.isLoading || top.isLoading || trendQ.isLoading || op.isLoading) && (
        <div className="mt-3" style={mono(10, "#94a3b8")}>
          <Loader2 size={11} className="inline animate-spin mr-2" />Refreshing slice…
        </div>
      )}
      </>
      )}

      {/* COPQ drill-down modal */}
      {copqDrillOpen && (
        <CopqDrillModal
          rows={copqQ.data?.drilldown_rows || []}
          truncated={copqQ.data?.drilldown_truncated}
          ccy={ccy}
          onClose={() => setCopqDrillOpen(false)}
        />
      )}
    </div>
  );
}
